import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { Error, MessagesSearch } from '@swyftx/aviary/icons/outlined';

import { observer } from 'mobx-react-lite';
import { MarginOrdersType } from 'src/lib/margin/types';

import { MarginAccountTransferModal } from '../../Account/MarginAccountTransferModal';

type Props = {
  ordersType: MarginOrdersType;
  isError?: boolean;
  action?: React.ReactNode;
  filtersActive?: boolean;
  onClearFilters?: () => void;
};

const MarginPositionsTableEmpty: React.FC<Props> = observer(
  ({ ordersType, action, filtersActive, isError, onClearFilters }) => {
    const { t } = useTranslation('margin');

    const title = useMemo(() => {
      if (isError) return t('tables.positions.empty.title.failed');

      switch (ordersType) {
        case MarginOrdersType.Position:
          return t('tables.positions.empty.title.open');
        case MarginOrdersType.Orders:
          return t('tables.pendingOrder.empty.title.pending');
        case MarginOrdersType.History:
          return t('tables.history.empty.title.history');
        case MarginOrdersType.Transfers:
          return t('tables.transfers.empty.title.transfers');
        case MarginOrdersType.BalanceAdjustments:
          return t('tables.balanceAdjustments.empty.title.adjustments');
        default:
          ordersType satisfies never;
          return '';
      }
    }, [isError, ordersType, t]);

    const content = useMemo(() => {
      if (filtersActive) return t('tables.positions.empty.content.filter');

      switch (ordersType) {
        case MarginOrdersType.Position:
          return isError ? t('tables.positions.empty.content.failed') : t('tables.positions.empty.content.open');
        case MarginOrdersType.Orders:
          return isError
            ? t('tables.pendingOrder.empty.content.failed')
            : t('tables.pendingOrder.empty.content.pending');
        case MarginOrdersType.History:
          return isError ? t('tables.history.empty.content.failed') : t('tables.history.empty.content.history');
        case MarginOrdersType.Transfers:
          return isError ? t('tables.transfers.empty.content.failed') : t('tables.transfers.empty.content.transfers');
        case MarginOrdersType.BalanceAdjustments:
          return isError
            ? t('tables.balanceAdjustments.empty.content.failed')
            : t('tables.balanceAdjustments.empty.content.adjustments');
        default:
          ordersType satisfies never;
          return '';
      }
    }, [filtersActive, isError, ordersType, t]);

    const actions = useMemo(() => {
      if (isError) return null;

      if (filtersActive)
        return <Button onClick={onClearFilters}>{t('tables.positions.empty.actions.clearFilters')}</Button>;
      if (action) return action;

      switch (ordersType) {
        case MarginOrdersType.Position:
        case MarginOrdersType.Orders:
        case MarginOrdersType.History:
        case MarginOrdersType.BalanceAdjustments:
          return null;
        case MarginOrdersType.Transfers:
          return (
            <MarginAccountTransferModal position='dynamic'>
              <Button>{t('tables.transfers.empty.actions.fund')}</Button>
            </MarginAccountTransferModal>
          );
        default:
          ordersType satisfies never;
          return '';
      }
    }, [action, filtersActive, isError, onClearFilters, ordersType, t]);

    return (
      <FlexLayout
        className='h-full w-full p-16'
        alignItems='center'
        justifyContent='center'
        direction='column'
        spacing={12}
      >
        {isError ? (
          <Error className='h-48 w-48 text-color-text-accent' />
        ) : (
          <MessagesSearch className='h-48 w-48 text-color-text-accent' />
        )}
        <Heading size='h3'>{title}</Heading>
        <Body className='text-center' color='secondary'>
          {content}
        </Body>
        {actions}
      </FlexLayout>
    );
  },
);

export { MarginPositionsTableEmpty };
